import { useState } from 'react';

import { Card, CardBody, CardHeader, CardTitle, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { useGhiPhien } from '../hooks/useBienBan';
import { chiSoNguoiNoi, dinhDangThoiLuong } from '../nghiep-vu/bien-ban';
import type { NguoiNoi } from '../types';
import { BadgeNguoiNoi } from './nhan-nguoi-noi';

function DongNguoiNoi({ phienId, nguoi, coSua }: { phienId: string; nguoi: NguoiNoi; coSua: boolean }) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const { sua } = useGhiPhien();
  const [ten, setTen] = useState(nguoi.name ?? '');
  const nhan = t('bienBan.loi.nguoiNoiSo', { so: soVN(chiSoNguoiNoi(nguoi.label) + 1, locale) });

  /** Rời ô hoặc Enter thì lưu; không đổi thì không gọi. Ô trống là bỏ tên, quay về "Người nói N". */
  const luu = () => {
    if (ten.trim() === (nguoi.name ?? '')) return;
    sua.mutate(
      { id: phienId, vao: { speakers: { [nguoi.label]: ten.trim() } } },
      {
        onSuccess: () => toast.success(t('bienBan.nguoiNoi.daLuu')),
      },
    );
  };

  return (
    <li className="space-y-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="w-24 shrink-0"><BadgeNguoiNoi label={nguoi.label} ten={nhan} /></span>
        {coSua ? (
          <Input
            size="small"
            className="min-w-0 flex-1 basis-32"
            aria-label={t('bienBan.nguoiNoi.nhanTen', { nhan })}
            aria-invalid={Boolean(sua.error)}
            placeholder={t('bienBan.nguoiNoi.datTen')}
            value={ten}
            maxLength={100}
            status={sua.error ? 'error' : undefined}
            onChange={(e) => setTen(e.target.value)}
            onBlur={luu}
            onPressEnter={(e) => {
              e.stopPropagation();
              luu();
            }}
            onKeyDown={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="min-w-0 flex-1 basis-32 truncate text-sm text-content-primary">{nguoi.name}</span>
        )}
        <span title={t('bienBan.nguoiNoi.noiTrong')} aria-label={`${t('bienBan.nguoiNoi.noiTrong')} ${dinhDangThoiLuong(nguoi.speech_seconds)}`} className="font-mono text-xs tabular-nums text-content-muted">
          {dinhDangThoiLuong(nguoi.speech_seconds)}
        </span>
      </div>
      {sua.error && <p role="alert" className="text-xs text-danger-600">{dichLoi(sua.error)}</p>}
    </li>
  );
}

/** Đặt tên người nói: một tên đổi khắp bản chép lời, ô đổi người nói và lần tóm tắt sau (`PATCH /sessions/:id`). */
export function NguoiNoiPhien({ phienId, speakers, coSua }: { phienId: string; speakers: readonly NguoiNoi[]; coSua: boolean }) {
  const { t } = useT();
  return (
    <Card>
      <CardHeader><CardTitle>{t('bienBan.nguoiNoi.tieuDe')}</CardTitle></CardHeader>
      <CardBody className="py-4">
        {speakers.length === 0 ? (
          <p className="text-xs text-content-muted">{t('bienBan.nguoiNoi.rong')}</p>
        ) : (
          <ul className="space-y-2.5">
            {speakers.map((n) => (
              // `key` có tên: máy chủ đổi tên thì ô dựng lại với tên mới.
              <DongNguoiNoi key={`${n.label}:${n.name ?? ''}`} phienId={phienId} nguoi={n} coSua={coSua} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
